<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompaniesForm from 'dashboard/components-next/Companies/CompaniesForm/CompaniesForm.vue';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'delete', 'avatarUpload', 'avatarDelete']);

const { t } = useI18n();

const companiesFormRef = ref(null);
const avatarFile = ref(null);
const avatarUrl = ref('');

const companyData = ref({});

// Safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
};

// Initialize with safe defaults
const initializeData = () => {
  if (props.company && typeof props.company === 'object') {
    companyData.value = { ...props.company };
    avatarUrl.value = props.company.avatarUrl || '';
  } else {
    companyData.value = {};
    avatarUrl.value = '';
  }
};

watch(() => props.company, initializeData, { immediate: true });

const isFormInvalid = computed(() => companiesFormRef.value?.isFormInvalid || false);

const createdAt = computed(() => {
  return formatDate(companyData.value?.createdAt);
});

const updatedAt = computed(() => {
  return formatDate(companyData.value?.updatedAt);
});

const avatarSrc = computed(() => {
  return avatarUrl.value || companyData.value?.avatarUrl || '';
});

const displayName = computed(() => {
  return companyData.value?.name || t('COMPANIES.UNNAMED');
});

const handleFormUpdate = updatedData => {
  Object.assign(companyData.value, updatedData);
};

const updateCompany = async () => {
  try {
    const dataToSend = { ...companyData.value };
    delete dataToSend.customAttributes;
    emit('update', dataToSend);
  } catch (error) {
    useAlert(t('COMPANIES.FORM.UPDATE_ERROR'));
  }
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
  emit('avatarUpload', { file, url });
};

const handleAvatarDelete = async () => {
  emit('avatarDelete');
  avatarFile.value = null;
  avatarUrl.value = '';
  if (companyData.value) {
    companyData.value.avatarUrl = null;
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc"
        :name="displayName"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ displayName }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span 
            v-if="createdAt || updatedAt" 
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-building text-n-slate-10 size-4" />
            <template v-if="createdAt">
              {{ $t('COMPANIES.DETAILS.CREATED_AT', { date: createdAt }) }}
            </template>
            <template v-if="updatedAt && updatedAt !== createdAt">
              •
              {{ $t('COMPANIES.DETAILS.UPDATED_AT', { date: updatedAt }) }}
            </template>
          </span>
        </div>
      </div>
    </div>
    
    <div class="flex flex-col items-start gap-6 w-full">
      <CompaniesForm
        ref="companiesFormRef"
        :company-data="companyData"
        @update="handleFormUpdate"
      />
      <Button
        :label="t('COMPANIES.FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateCompany"
      />
    </div>
    
    <div
      class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
    >
      <div class="flex flex-col gap-2">
        <h6 class="text-base font-medium text-n-slate-12">
          {{ t('COMPANIES.FORM.DELETE_COMPANY') }}
        </h6>
        <span class="text-sm text-n-slate-11">
          {{ t('COMPANIES.FORM.DELETE_COMPANY_DESCRIPTION') }}
        </span>
      </div>
      <Button
        :label="t('COMPANIES.FORM.DELETE_COMPANY')"
        color="ruby"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>
